import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, inject, Injectable } from '@angular/core';
import { TimeoutError } from 'rxjs';
import { ServerErrorCodes, TranslationKeys } from 'src/app/contracts/index';
import { CoreErrorCodes, HttpError, InternalError, IServerError } from 'src/app/core/contracts/index';
import { Logger } from 'src/app/core/helpers/logger';
import { UI } from 'src/app/core/helpers/ui';
import { isDev } from 'src/app/core/helpers/utils';

@Injectable()
export class AppErrorHandler implements ErrorHandler {
  private ui = inject(UI);
  private logger = inject(Logger);

  public handleError(err: Error | InternalError | HttpError): void {
    // debugger;
    this.logger.error(err);
    if (err instanceof InternalError) {
      this.handleInternalError(err);
    } else if (err instanceof HttpError) {
      this.handleHttpError(err);
    } else {
      this.ui.showError(isDev ? err?.message : undefined);
    }
  }
  private handleInternalError(err: InternalError) {
    if (err.handleError) {
      const error = TranslationKeys.Errors[err.code as keyof typeof TranslationKeys.Errors];
      switch (err.code) {
        case CoreErrorCodes.Offline:
          this.ui.showToast(error);
          break;
        case CoreErrorCodes.GeolocationPositionError:
        case CoreErrorCodes.LocationPermissionDenied:
          this.ui.showError(error);
          break;
        default:
          this.ui.showError(isDev ? err.message : error);
      }
    }
  }
  private handleHttpError(httpError: HttpError): void {
    if (httpError.options.handleError) {
      const response = httpError.response;
      if (response instanceof HttpErrorResponse && isServerError(response.error)) {
        const serverError = response.error;
        if (isModelValidationServerError(serverError)) {
          const message = serverError.details.map(detail => detail.message).join('. ');
          this.ui.showError(message);
        } else if (serverError.message) {
          this.ui.showError(serverError.message);
        } else {
          this.ui.showError(TranslationKeys.ServerErrors[serverError.code as keyof typeof TranslationKeys.ServerErrors]);
        }
      } else if (response instanceof TimeoutError) {
        this.ui.showError(TranslationKeys.Errors.ConnectionFailedPleaseTryAgain);
      } else if (response.status === 0) {
        this.ui.showError(TranslationKeys.Errors.CantReachTheServer);
      } else {
        this.ui.showError(isDev ? httpError.message : undefined);
      }
    }
  }
  private normalizeMessage(message: string | undefined) {
    return message
      ?.replace(/\s+/g, '') // remove whitespaces
      .replace(/[`~!@#$%^&*()_|+\-=?;:'",.<>\{\}\[\]\\\/]/gi, ''); // remove special characters
  }
}
export function isUnknownError(err: unknown) {
  return !(err instanceof InternalError || err instanceof HttpError);
}
export function isServerError(error: unknown): error is IServerError {
  // error response format from api is: { error: ServerErrorType }
  // ServerErrorType should have a code property
  return error != null && typeof error === 'object' && 'code' in error;
}
export function isModelValidationServerError(error: unknown) {
  return isServerError(error) && error.code === ServerErrorCodes.InvalidModel && error.details?.length > 0;
}
export function hasErrorCode(err: HttpError | InternalError, code: string) {
  if (err && err instanceof HttpError && err.response instanceof HttpErrorResponse && isServerError(err.response.error)) {
    return err.response.error.code === code;
  }
  return false;
}
export function getServerError(err: HttpError | InternalError): IServerError {
  if (err && err instanceof HttpError && err.response instanceof HttpErrorResponse && isServerError(err.response.error)) {
    return err.response.error;
  }
  return null;
}
